import { CheckboxComponent } from '../../UI/CheckboxComponent/CheckboxComponent';
import { MenuItemComponent } from '../../UI/MenuItemComponent/MenuItemComponent';
import './FilterComponent.scss';
import {
	FilterState,
	handleToolTipClick,
	orderFiltersAlphabetically,
	validateScroll
} from './model';
import { FC, useEffect, useRef, useState, useCallback } from 'react';
import {
	FilterLevel,
	FilterType,
	FilterVM,
	PintoFilters,
	ScreenType,
	viewModeType
} from '../../../common/view-model';
import { RootState } from '../../../redux/AppReducer';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { FilterTooltipComponent } from '../tooltip/FilterTooltipComponent';
import { FilterActions } from '../../../redux/FilterActions';
import { handleTracking } from '../../../helpers/handelTracking';
import { v4 as uuidv4 } from 'uuid';
import { TrackingEventStatus } from '../../../common/view-model';
import {
	flatSecondLevelFilters,
	flatThirdLevelChildren,
	getFilterTypeKeyForUrl
} from '../../../helpers/urlParametersHelpers';
import { CategoryFilterComponent } from '../category-filter/CategoryFilterComponent';
import { isCurrentUserAnonymous } from '../../../helpers/authenticationHelper';
import LockOverlayComponent from '../../LockOverlay/LockOverlayComponent';
import { AppActions } from '../../../redux/AppActions';
import { UserActions } from '../../../redux/UserActions';
import { publicProductDiscoveryActions } from '../../../common/constants';
import { useAppDispatch } from '../../../redux/store';
import { useSelector } from 'react-redux';
interface IMainFilterComponentProps {
	filter: PintoFilters;
	openIndex: number | undefined;
	parentIndex: number;
	setOpenIndex: (index: number | undefined) => void;
	deviceScreen?: ScreenType;
	isMobile?: boolean;
	pendingFilters?: FilterVM[];
	setPendingFilters?: React.Dispatch<React.SetStateAction<FilterVM[]>>;
	selectedFilters?: FilterVM[];
}
interface IFilterComponentProps {
	onClose: () => void;
}
export const MainFilterComponent: FC<IMainFilterComponentProps> = ({
	filter,
	openIndex,
	setOpenIndex,
	parentIndex,
	deviceScreen,
	pendingFilters,
	setPendingFilters
}) => {
	const dispatch = useAppDispatch();
	const [filterComponentState] = useState({ ...FilterState, filter });
	filterComponentState.multifilterItems = [];
	const size = useWindowSize();
	const [deviceViewMode, setDeviceViewMode] = useState<viewModeType>(
		size.width > 768 ? viewModeType.DESKTOP : viewModeType.MOBILE
	);
	const { currentFilterOpenIndex } = useSelector(
		(state: RootState) => state.appState.filterState || {}
	);
	const { selectedFilters, openFilterFunctionRef, carrotIconClicked, selectedCategory } =
		useSelector((state: RootState) => state.appState.filterState || {});
	const { user } = useSelector((state: RootState) => state.appState.userState || {});

	const parentFilters = useSelector(
		(state: RootState) =>
			state.appState.filterState?.allFilters?.filter(f => f.type === FilterType.CATEGORY)[0]
				?.filters || []
	);

	const filterRef = useRef<HTMLDivElement>(null);
	const filterContainerRef = useRef<HTMLDivElement>(null);
	const maxScrollHeigth = 520;
	const [isDeskTopScreen, setIsDeskTopScreen] = useState<boolean>(
		deviceScreen === ScreenType.LARGE_DESKTOP ||
			deviceScreen === ScreenType.MEDIUM_DESKTOP ||
			deviceScreen === ScreenType.SMALL_DESKTOP
	);
	const isMobile = deviceScreen === ScreenType.MOBILE ? true : false;
	const setOpenFilterRef = (setOpen: () => void) =>
		dispatch(FilterActions.addOpenFilterFunctionRef(setOpen));
	const setBubbleFuction = (type: FilterType, value: () => void, filterRef: any) =>
		dispatch(FilterActions.setBubbleFunctionRef({ type, value, filterRef }));
	const clearBubbleFunctionRef = () => dispatch(FilterActions.clearBubbleFunctionRef());
	const carrotIconClickCallback = useCallback(
		(carrotIconOpen: boolean) => {
			dispatch(FilterActions.setCarrotIconClick(carrotIconOpen));
		},
		[dispatch]
	);
	const setCurrentCategory = (category?: string) => dispatch(FilterActions.setCategory(category));
	const addFilter = (filter: FilterVM) => dispatch(FilterActions.addFilter(filter));
	const setLockModalContent = () =>
		dispatch(
			AppActions.setModalContent({
				content: <LockOverlayComponent />,
				showCloseIcon: true,
				canScroll: false,
				closeOnDocumentClick: true
			})
		);
	const setHasClickOnFilter = (hasClicked: boolean) =>
		dispatch(UserActions.clickOnFilter(hasClicked));
	const registerUserClickEvent = (event: string) =>
		dispatch(UserActions.registerUserClickEvent(event));
	const clearAllFilters = () => dispatch(FilterActions.clearAllFilters());
	const handleClick = (index: number | undefined, filterName: string | undefined) => {
		if (isCurrentUserAnonymous() && openIndex !== index) {
			registerUserClickEvent(publicProductDiscoveryActions.CLICK_OPEN_FILTER);
		}
		setOpenIndex(openIndex === index ? undefined : index);
		if (openIndex !== index) {
			handleTracking({
				transactionId: uuidv4(),
				entityId: filter.id!,
				status: TrackingEventStatus.SUCCESS,
				type: `Click on ${filterName}`,
				timeStamp: new Date().toDateString(),
				eventPayload: {
					id: filter.id!,
					name: filter.name!,
					type: getFilterTypeKeyForUrl(filter.type!),
					url: window.location.href,
					tags: [],
					metadata: {
						user_id: user?.id,
						user_type: user?.user_type,
						environment: import.meta.env.VITE_ENV_NAME,
						filter_id: filter.id,
						filter_name: filter.name
					}
				}
			});
		}
	};
	const onTooltipClick = (value: string, iconId: string) => {
		handleToolTipClick(value, iconId);
		carrotIconClickCallback(true);
	};
	const handleAnonymousClick = () => {
		setHasClickOnFilter(true);
		setLockModalContent();
	};
	useEffect(() => {
		if (size.width < 768) {
			setDeviceViewMode(viewModeType.MOBILE);
		} else {
			setDeviceViewMode(viewModeType.DESKTOP);
		}
	}, [size]);
	useEffect(() => {
		if (filter.type === FilterType.CATEGORY) {
			setOpenFilterRef(() => setOpenIndex(1));
		}
	}, []);
	useEffect(() => {
		if (selectedFilters && selectedFilters.length > 0 && openFilterFunctionRef) {
			const lastFilter = selectedFilters[selectedFilters.length - 1];
			if (
				lastFilter.type === FilterType.CATEGORY ||
				lastFilter.type === FilterType.WHATS_NEW ||
				lastFilter.type === FilterType.NEW_TO_BEACON ||
				lastFilter.type === FilterType.NEW_TO_MARKET ||
				lastFilter.type === FilterType.NEXTY_FINALISTS
			) {
				openFilterFunctionRef();
			}
		}
	}, [selectedFilters, openFilterFunctionRef]);
	useEffect(() => {
		if (
			carrotIconClicked &&
			openIndex === parentIndex &&
			filterRef?.current &&
			filterRef.current.scrollHeight > maxScrollHeigth
		) {
			filterRef.current.classList.add('scrollable');
		} else {
			filterRef.current?.classList.remove('scrollable');
		}
	}, [openIndex, parentIndex, carrotIconClicked]);
	useEffect(() => {
		if (!filterContainerRef.current) return;
		if (parentIndex === 0 && setBubbleFuction) {
			setBubbleFuction(FilterType.NEW_TO_MARKET, () => setOpenIndex(0), null);
			setBubbleFuction(FilterType.NEW_TO_BEACON, () => setOpenIndex(0), null);
			setBubbleFuction(FilterType.NEXTY_FINALISTS, () => setOpenIndex(0), null);
		}
		setBubbleFuction &&
			setBubbleFuction(filter.type, () => setOpenIndex(parentIndex), filterContainerRef.current);
	}, [filter, parentIndex, filterContainerRef]);

	useEffect(() => {
		return () => {
			clearBubbleFunctionRef && clearBubbleFunctionRef();
		};
	}, []);
	useEffect(() => {
		if (deviceScreen === ScreenType.TABLET) {
			if (currentFilterOpenIndex !== undefined) {
				setOpenIndex(currentFilterOpenIndex);
			}
		} else {
			setOpenIndex(undefined);
		}
	}, [currentFilterOpenIndex, deviceScreen, setOpenIndex]);
	useEffect(() => {
		setIsDeskTopScreen(
			deviceScreen === ScreenType.LARGE_DESKTOP ||
				deviceScreen === ScreenType.MEDIUM_DESKTOP ||
				deviceScreen === ScreenType.SMALL_DESKTOP
		);
	}, [deviceScreen]);
	return (
		<div
			ref={filterContainerRef}
			data-testid='filter-container'
			className={`filter-container ${
				deviceViewMode === viewModeType.DESKTOP ? 'bottom-border' : ''
			} ${
				filter.type === FilterType.CATEGORY && openIndex === 1 ? 'open-filter' : 'collapsed-filter'
			}`}
		>
			<div className='row'>
				<div
					className={`main-filter-container ${
						filter.type === FilterType.CATEGORY && openIndex === 1 ? 'category-wrapper' : ''
					}`}
					onClick={!isDeskTopScreen ? () => handleClick(parentIndex, filter.name) : () => {}}
				>
					<span className='main-filter-name'>{filter.name}</span>
					<span
						data-testid='main-filter-name'
						className='icon-wrapper'
						onClick={() => handleClick(parentIndex, filter.name)}
					>
						{isCurrentUserAnonymous() ? (
							<i className='ri-lock-line'></i>
						) : (
							<button
								id={
									openIndex === parentIndex
										? `${filter.name?.replace(/\s+/g, '').trim().toLowerCase()}-opened`
										: `${filter.name?.replace(/\s+/g, '').trim().toLowerCase()}-closed`
								}
								type='button'
								className={`${
									openIndex === parentIndex
										? 'main-menu-close ri-subtract-line'
										: 'main-menu-open ri-add-line'
								}`}
							></button>
						)}
					</span>
				</div>
			</div>
			<div className='row'>
				{openIndex === parentIndex && (
					<div
						id={`filter-content${filter.id}`}
						ref={filterRef}
						className={`filter-content ${
							filter.type === FilterType.CATEGORY ? 'cat-margin-wrapper' : ''
						} ${
							validateScroll(`filter-content${filter.id}`, openIndex === parentIndex)
								? 'scrollable'
								: ''
						}`}
					>
						{filter?.filters?.map((item: FilterVM, index: number) => {
							return (
								<div key={index}>
									{item.children.length === 0 && item.type !== FilterType.CATEGORY ? (
										<>
											<div className='filter-tooltip-container'>
												<CheckboxComponent
													checked={item.isChecked}
													label={item.name}
													style='primary'
													parentIndex={item.id}
													index={index}
													level={FilterLevel.FIRST}
													type={item.type}
													parentLength={filter.filters.length}
													isLocked={isCurrentUserAnonymous()}
													setLockModalContent={handleAnonymousClick}
													registerUserClickEvent={registerUserClickEvent}
													pendingFilters={pendingFilters}
													setPendingFilters={setPendingFilters}
												/>
												{item.toolTip?.description && (
													<i
														className='ri-question-line tooltip-icon'
														id={`toltip-icon${item?.id || ''}`}
														onClick={() =>
															onTooltipClick(
																`${item.type}${item.id}`,
																`toltip-icon${item?.id || ''}`
															)
														}
													></i>
												)}
											</div>
											{item.toolTip?.description && (
												<FilterTooltipComponent
													id={`${item.type}${item.id}`}
													toolTip={item.toolTip}
												/>
											)}
										</>
									) : (
										<>
											{filter.type !== FilterType.CATEGORY && (
												<>
													{filterComponentState.multifilterItems.indexOf(filter) === -1 ? (
														<MenuItemComponent
															items={filter.filters}
															setLockModalContent={setLockModalContent}
															registerUserClickEvent={registerUserClickEvent}
														/>
													) : undefined}
													{filterComponentState.multiFilterHelper(
														filterComponentState.multifilterItems,
														filter
													)}
												</>
											)}
										</>
									)}
								</div>
							);
						})}
						{filter.type === FilterType.CATEGORY && (
							<CategoryFilterComponent
								filters={orderFiltersAlphabetically(filter.filters) || []}
								currentCategory={selectedCategory}
								setCurrentCategory={setCurrentCategory!}
								parentFilters={parentFilters}
								thirdLevelChildren={flatThirdLevelChildren(filter.filters)}
								secondLevelChildren={flatSecondLevelFilters(filter.filters)}
								addFilter={addFilter}
								selectedFilters={selectedFilters}
								userState={user}
								setLockModalContent={setLockModalContent}
								registerUserClickEvent={registerUserClickEvent}
								clearAllFilters={clearAllFilters}
								isMobile={isMobile}
								pendingFilters={pendingFilters}
								setPendingFilters={setPendingFilters}
							/>
						)}
					</div>
				)}
			</div>
			{deviceViewMode === viewModeType.MOBILE && <div className='line'></div>}
		</div>
	);
};

// const FilterComponent: FC<IFilterComponentProps> = ({ onClose }) => {
// 	const dispatch = useAppDispatch();
// 	const deviceScreen = useSelector((state: RootState) => state.appState.deviceScreen);
// 	const { allFilters } = useSelector((state: RootState) => state.appState.filterState || {});
// 	const [openIndex, setOpenIndex] = useState<number | undefined>();
// 	const setCurrentFilterOpenIndex = (index: number) =>
// 		dispatch(FilterActions.setCurrentFilterOpenIndex(index));
// 	useEffect(() => {
// 		if (deviceScreen === ScreenType.TABLET && openIndex !== undefined) {
// 			setCurrentFilterOpenIndex(openIndex);
// 		}
// 	}, [openIndex, deviceScreen]);
// 	const isMobile = deviceScreen === ScreenType.MOBILE ? true : false;
// 	const [pendingFilters, setPendingFilters] = useState<FilterVM[]>([]);
// 	const [originalPendingFilters, setOriginalPendingFilters] = useState<FilterVM[]>([]);

// useEffect(() => {
//         if (pendingFilters.length > 0 && originalPendingFilters.length === 0) {
//             setOriginalPendingFilters(pendingFilters.map(f => ({ ...f })));
//         }
//     }, [pendingFilters]);

// 	const handleApplyFilters = () => {
// 		pendingFilters.forEach(filter => {
// 			if (filter.isChecked) {
// 				dispatch(FilterActions.addFilter(filter));
// 			} else {
// 				dispatch(FilterActions.removeFilter(filter, true)); // true = isMobile
// 			}
// 		});
// 		setPendingFilters([]);
// 		onClose()
// 	};

// 	const [disableButton, setDisableButton] = useState<boolean>(true);

// const selectedFilters = useSelector(
//     (state: RootState) => state.appState.filterState?.selectedFilters
// );

// useEffect(() => {
//     if (!pendingFilters || !selectedFilters) {
//         setDisableButton(true);
//         return;
//     }

//     const hasChanges = pendingFilters.some(pending => {
//         const original = selectedFilters.find(sel => sel.id === pending.id);

//         // If filter is new or its checked state has changed
//         return !original || original.isChecked !== pending.isChecked;
//     });

//     setDisableButton(!hasChanges);
// }, [pendingFilters, selectedFilters]);

// 	return (
// 		<div className="page-container">
// 			<div className="filters-scrollable">
// 				<div className="filters-container">
// 					{allFilters && allFilters.length > 0 && (
// 						<>
// 							{allFilters.map((filter, index) => (
// 								<MainFilterComponent
// 									key={index}
// 									filter={filter}
// 									parentIndex={index}
// 									setOpenIndex={setOpenIndex}
// 									openIndex={openIndex}
// 									deviceScreen={deviceScreen}
// 									isMobile={isMobile}
// 									pendingFilters={pendingFilters}
// 									setPendingFilters={setPendingFilters}
// 									selectedFilters={selectedFilters}
// 								/>
// 							))}
// 						</>
// 					)}
// 				</div>
// 			</div>
// 			{isMobile &&
// 				<div className={`bottom ${disableButton ? 'disabled-style' : ''}`}>
// 					<div className="filter-buttons">
// 						<button className='apply-filter'
// 							onClick={handleApplyFilters}
// 							disabled={disableButton}
// 						>Apply Filters</button>
// 						<div className="cancel-filter" onClick={onClose} >Cancel</div>
// 					</div>
// 				</div>}
// 		</div>
// 	);
// };

// export default FilterComponent;

const FilterComponent: FC<IFilterComponentProps> = ({ onClose }) => {
	const dispatch = useAppDispatch();
	const deviceScreen = useSelector((state: RootState) => state.appState.deviceScreen);
	const { allFilters } = useSelector((state: RootState) => state.appState.filterState || {});
	const selectedFilters = useSelector(
		(state: RootState) => state.appState.filterState?.selectedFilters
	);

	const [openIndex, setOpenIndex] = useState<number | undefined>();
	const [pendingFilters, setPendingFilters] = useState<FilterVM[]>([]);
	const [disableButton, setDisableButton] = useState<boolean>(true);

	const isMobile = deviceScreen === ScreenType.MOBILE;

	const setCurrentFilterOpenIndex = (index: number) =>
		dispatch(FilterActions.setCurrentFilterOpenIndex(index));

	useEffect(() => {
		if (deviceScreen === ScreenType.TABLET && openIndex !== undefined) {
			setCurrentFilterOpenIndex(openIndex);
		}
	}, [openIndex, deviceScreen]);

	// Enable Apply button only if there's a net change from Redux state
	useEffect(() => {
		if (!pendingFilters || !selectedFilters) {
			setDisableButton(true);
			return;
		}

		const hasChanges = pendingFilters.some(pending => {
			const original = selectedFilters.find(sel => sel.id === pending.id);
			// If not in selectedFilters, treat as unchecked originally
			const originalChecked = original?.isChecked ?? false;
			return originalChecked !== pending.isChecked;
		});

		setDisableButton(!hasChanges);
	}, [pendingFilters, selectedFilters]);

	const handleApplyFilters = () => {
		pendingFilters.forEach(filter => {
			if (filter.isChecked) {
				dispatch(FilterActions.addFilter(filter));
			} else {
				dispatch(FilterActions.removeFilter(filter, true)); // true = isMobile
			}
		});
		setPendingFilters([]);
		onClose();
	};

	return (
		<div className='page-container'>
			<div className='filters-scrollable'>
				<div className='filters-container'>
					{allFilters && allFilters.length > 0 && (
						<>
							{allFilters.map((filter, index) => (
								<MainFilterComponent
									key={index}
									filter={filter}
									parentIndex={index}
									setOpenIndex={setOpenIndex}
									openIndex={openIndex}
									deviceScreen={deviceScreen}
									isMobile={isMobile}
									pendingFilters={pendingFilters}
									setPendingFilters={setPendingFilters}
									selectedFilters={selectedFilters}
								/>
							))}
						</>
					)}
				</div>
			</div>

			{isMobile && (
				<div className={`bottom ${disableButton ? 'disabled-style' : ''}`}>
					<div className='filter-buttons'>
						<button className='apply-filter' onClick={handleApplyFilters} disabled={disableButton}>
							Apply Filters
						</button>
						<div className='cancel-filter' onClick={onClose}>
							Cancel
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default FilterComponent;
